import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import type { SimulationActivity } from '../../../../types/simulation';
import type { VehicleOperation, ActivityNode, ActivityNodeType } from './useVehicleOperations';
import { liftangoBrandTokens } from '../../../../theme';
import { resolveActivityStopAddress, resolveLegAddress } from './utils/addressResolution';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
const FONT_FAMILY = '"Roboto", sans-serif';
const GREEN = liftangoBrandTokens.visualization.pickupNode;
const ORANGE = liftangoBrandTokens.visualization.dropoffNode;
const TARMAC = liftangoBrandTokens.visualization.neutralNode;
/** Service start/end circles: visible on dark sidebar (neutralNode matches background) */
const SHIFT_NODE = liftangoBrandTokens.visualization.shiftNodeOnDark ?? TARMAC;
const SIDEBAR = {
  background: liftangoBrandTokens.simulationResults.sidebarBackground,
  text: liftangoBrandTokens.simulationResults.sidebarText,
  muted: liftangoBrandTokens.simulationResults.sidebarMutedText,
  border: liftangoBrandTokens.simulationResults.sidebarBorder,
};

/** Return lat/lng only when both are finite; otherwise null (for map highlight guard). */
export function getActivityCoordinates(
  activity: SimulationActivity
): { lat: number; lng: number } | null {
  const lat =
    activity.activity_latitude ??
    activity.latitude ??
    (activity as Record<string, unknown>).lat;
  const lng =
    activity.activity_longitude ??
    activity.longitude ??
    (activity as Record<string, unknown>).lng;
  if (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng)
  ) {
    return { lat, lng };
  }
  return null;
}

/** Location label from optional fields; fallback to coords or translated "Location". Null/empty safe. */
export function getActivityLocationLabel(
  activity: SimulationActivity,
  t: TFunction<'simulation'>
): string {
  const str = resolveActivityStopAddress(activity);
  if (str && str.length > 0) return str;
  const coords = getActivityCoordinates(activity);
  if (coords) {
    return `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`;
  }
  return t('results.driverActivity.locationFallback');
}

export function getActivityLegAddressLabel(
  activity: SimulationActivity,
  leg: 'pickup' | 'dropoff',
  t: TFunction<'simulation'>
): string {
  const resolved = resolveLegAddress({ leg, activity, fallbackToStopAddress: true, fallbackToCoords: true });
  return resolved ?? t('results.driverActivity.locationFallback');
}

/** metres */
function haversineMeters(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function isOperationalNodeType(t: ActivityNodeType): boolean {
  return t === 'SHIFT_START' || t === 'SHIFT_END' || t === 'OTHER';
}

/**
 * True when this node is an OTHER ("Stop") row that duplicates another operational row:
 * - Same time/place as the previous operational node (e.g. duplicate depot at service start), or
 * - Same time/place as the following Service End row (API duplicate before shift end).
 */
export function isRedundantOperationalDuplicate(nodes: ActivityNode[], index: number): boolean {
  const curr = nodes[index];
  if (curr.type !== 'OTHER') return false;

  const next = index + 1 < nodes.length ? nodes[index + 1] : null;
  if (next?.type === 'SHIFT_END') {
    if (Math.abs(curr.timeMs - next.timeMs) <= 90_000) {
      const c = getActivityCoordinates(curr.activity);
      const n = getActivityCoordinates(next.activity);
      if (c && n && haversineMeters(c.lat, c.lng, n.lat, n.lng) <= 150) {
        return true;
      }
    }
  }

  if (index <= 0) return false;
  const prev = nodes[index - 1];
  if (!isOperationalNodeType(prev.type)) return false;
  if (Math.abs(curr.timeMs - prev.timeMs) > 90_000) return false;
  const c = getActivityCoordinates(curr.activity);
  const p = getActivityCoordinates(prev.activity);
  if (!c || !p) return false;
  return haversineMeters(c.lat, c.lng, p.lat, p.lng) <= 150;
}

/** Standard and wheelchair counts from activity (for pickup/dropoff). Null/empty safe. */
export function getPassengerCounts(
  activity: SimulationActivity,
  _type: 'pickup' | 'dropoff'
): { standard: number; wheelchair: number } {
  const raw = activity as Record<string, unknown>;
  // Explicit counts if present
  let standard =
    typeof raw.standard_passengers === 'number' && Number.isFinite(raw.standard_passengers)
      ? raw.standard_passengers
      : typeof raw.standard_count === 'number' && Number.isFinite(raw.standard_count)
        ? raw.standard_count
        : NaN;
  let wheelchair =
    typeof raw.wheelchair_passengers === 'number' && Number.isFinite(raw.wheelchair_passengers)
      ? raw.wheelchair_passengers
      : typeof raw.wheelchair_count === 'number' && Number.isFinite(raw.wheelchair_count)
        ? raw.wheelchair_count
        : NaN;
  // From passengers array: [{ type: 'standard'|'wheelchair', number_of_passengers: n }]
  if ((Number.isNaN(standard) || Number.isNaN(wheelchair)) && Array.isArray(raw.passengers)) {
    let s = 0;
    let w = 0;
    for (const p of raw.passengers) {
      if (p != null && typeof p === 'object') {
        const type = String((p as Record<string, unknown>).type ?? '').toLowerCase();
        const n = Number((p as Record<string, unknown>).number_of_passengers);
        const count = Number.isFinite(n) && n > 0 ? n : 1;
        if (type.includes('wheelchair')) w += count;
        else s += count;
      }
    }
    if (Number.isNaN(standard)) standard = s;
    if (Number.isNaN(wheelchair)) wheelchair = w;
  }
  // Fallback: total count as standard, 0 wheelchair
  const total =
    raw.passengers_count ?? raw.number_of_passengers;
  const totalN =
    typeof total === 'number' && Number.isFinite(total) && total >= 0 ? total : 1;
  if (Number.isNaN(standard) && Number.isNaN(wheelchair)) {
    standard = totalN;
    wheelchair = 0;
  } else if (Number.isNaN(standard)) {
    standard = Math.max(0, totalN - wheelchair);
  } else if (Number.isNaN(wheelchair)) {
    wheelchair = 0;
  }
  return {
    standard: Math.max(0, standard),
    wheelchair: Math.max(0, wheelchair),
  };
}

/** Format time from activity in UTC (HH:mm) so shift times match simulation data; "—" if missing. */
function formatActivityTime(activity: SimulationActivity): string {
  const v =
    activity.activity_start_time ??
    activity.activity_start ??
    activity.min_activity_start_time ??
    activity.start_time ??
    (activity as Record<string, unknown>).time;
  if (v != null && typeof v === 'string') {
    const ms = Date.parse(v);
    if (!Number.isNaN(ms)) {
      const d = new Date(ms);
      return `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}`;
    }
  }
  return '—';
}

/** Format a timestamp (ms) for the header: UTC HH:mm, dd/MM/yyyy so operating hours match simulation. */
function formatShiftTime(ms: number): string {
  const d = new Date(ms);
  const h = d.getUTCHours().toString().padStart(2, '0');
  const min = d.getUTCMinutes().toString().padStart(2, '0');
  const day = d.getUTCDate().toString().padStart(2, '0');
  const month = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = d.getUTCFullYear();
  return `${h}:${min}, ${day}/${month}/${year}`;
}

/** Human-readable label for activity type (Service Start, Pickup, Dropoff, Break Start, Break End, Service End). */
function getActivityTypeLabel(node: ActivityNode, t: TFunction<'simulation'>): string {
  const rawType = (node.activity.activity_type ?? node.activity.type ?? '')
    .toString()
    .toLowerCase();
  if (rawType.includes('break')) {
    return rawType.includes('end') || rawType.includes('finish')
      ? t('results.driverActivity.types.breakEnd')
      : t('results.driverActivity.types.breakStart');
  }
  switch (node.type) {
    case 'SHIFT_START':
      return t('results.driverActivity.types.serviceStart');
    case 'SHIFT_END':
      return t('results.driverActivity.types.serviceEnd');
    case 'OTHER':
      return rawType.includes('break')
        ? rawType.includes('end') || rawType.includes('finish')
          ? t('results.driverActivity.types.breakEnd')
          : t('results.driverActivity.types.breakStart')
        : t('results.driverActivity.types.stop');
    case 'PICK':
      return t('results.driverActivity.types.pickup');
    case 'DROP':
      return t('results.driverActivity.types.dropoff');
    default:
      return rawType.includes('pick')
        ? t('results.driverActivity.types.pickup')
        : rawType.includes('drop')
          ? t('results.driverActivity.types.dropoff')
          : t('results.driverActivity.types.activity');
  }
}

export interface DriverActivityDetailPanelProps {
  vehicle: VehicleOperation | null;
  onClose: () => void;
  runId?: string | null;
  /** When provided and activity has valid coords, called on row click to highlight on map and center. */
  onActivitySelect?: (activity: SimulationActivity) => void;
}

const DriverActivityDetailPanel: React.FC<DriverActivityDetailPanelProps> = ({
  vehicle,
  onClose,
  onActivitySelect,
}) => {
  const { t } = useTranslation('simulation');
  const visibleNodes = React.useMemo(
    () =>
      (vehicle?.nodes ?? []).filter(
        (_node, i, arr) => !isRedundantOperationalDuplicate(arr, i)
      ),
    [vehicle?.nodes]
  );

  if (!vehicle) return null;

  const shiftStartLabel =
    vehicle.shiftStart != null && Number.isFinite(vehicle.shiftStart)
      ? formatShiftTime(vehicle.shiftStart)
      : '—';
  const shiftEndLabel =
    vehicle.shiftEnd != null && Number.isFinite(vehicle.shiftEnd)
      ? formatShiftTime(vehicle.shiftEnd)
      : '—';

  const handleRowClick = (node: ActivityNode) => {
    if (!onActivitySelect) return;
    // Always select the activity (updates trip focus + details); map-centering is handled by the caller when coords exist.
    onActivitySelect(node.activity);
  };

  return (
    <Box
      sx={{
        width: 320,
        minWidth: 320,
        maxWidth: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: SIDEBAR.background,
        borderRadius: 2,
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          p: 2,
          borderBottom: `1px solid ${SIDEBAR.border}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: '1rem',
              fontWeight: 700,
              color: liftangoBrandTokens.palette.primary,
              lineHeight: 1.2,
            }}
          >
            {vehicle.displayName ?? vehicle.vehicleId}
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: '0.75rem',
              color: SIDEBAR.muted,
              mt: 0.5,
            }}
          >
            {shiftStartLabel} – {shiftEndLabel}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: SIDEBAR.text,
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
          }}
          aria-label={t('results.driverActivity.closePanel')}
        >
          <Icon name="xmark" size={20} />
        </IconButton>
      </Box>

      {/* Activity list */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          py: 1,
        }}
      >
        {visibleNodes.length === 0 ? (
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: '0.875rem',
              color: SIDEBAR.muted,
              p: 2,
            }}
          >
            {t('results.driverActivity.noActivities')}
          </Typography>
        ) : (
          visibleNodes.map((node, index) => {
            const isPick = node.type === 'PICK';
            const isDrop = node.type === 'DROP';
            const isShift =
              node.type === 'SHIFT_START' || node.type === 'SHIFT_END' || node.type === 'OTHER';
            const clickable = Boolean(onActivitySelect);

            return (
              <Box
                key={`${node.activity.ride_id ?? index}-${node.timeMs}-${index}`}
                onClick={() => clickable && handleRowClick(node)}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  cursor: clickable ? 'pointer' : 'default',
                  borderBottom: `1px solid ${SIDEBAR.border}`,
                  '&:hover': clickable
                    ? { backgroundColor: 'rgba(0,0,0,0.03)' }
                    : undefined,
                }}
              >
                {/* Left column: time, then icon, then activity type label (stacked, center-aligned) */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0, width: 56 }}>
                  <Typography
                    sx={{
                      fontFamily: FONT_FAMILY,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: SIDEBAR.text,
                      textAlign: 'center',
                    }}
                  >
                    {formatActivityTime(node.activity)}
                  </Typography>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isShift
                        ? SHIFT_NODE
                        : isPick
                          ? GREEN
                          : isDrop
                            ? ORANGE
                            : TARMAC,
                    }}
                  >
                    {isShift && (
                      <Icon size={16} name="bus" style={{ color: '#fff' }}
                        aria-hidden />
                    )}
                    {!isShift && isPick && (
                      <Icon size={16} d={ICON_FALLBACKS.ArrowUpward} style={{ color: '#fff' }}
                        aria-hidden />
                    )}
                    {!isShift && isDrop && (
                      <Icon size={16} name="arrow-down-long" style={{ color: '#fff' }}
                        aria-hidden />
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: FONT_FAMILY,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: SIDEBAR.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em',
                      textAlign: 'center',
                    }}
                  >
                    {getActivityTypeLabel(node, t)}
                  </Typography>
                </Box>
                {/* Right: location and passenger counts (icons + numbers) */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: FONT_FAMILY,
                      fontSize: '0.8125rem',
                      color: SIDEBAR.text,
                      lineHeight: 1.3,
                    }}
                  >
                    {getActivityLocationLabel(node.activity, t)}
                  </Typography>
                  {!isShift && (isPick || isDrop) && (
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '0.72rem',
                        color: SIDEBAR.muted,
                        lineHeight: 1.25,
                        mt: 0.2,
                      }}
                    >
                      {getActivityLegAddressLabel(node.activity, isPick ? 'pickup' : 'dropoff', t)}
                    </Typography>
                  )}
                  {!isShift && (() => {
                    const { standard, wheelchair } = getPassengerCounts(node.activity, isPick ? 'pickup' : 'dropoff');
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mt: 0.25, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }} aria-label={t('results.driverActivity.standardPassengersAria', { count: standard })}>
                          <Icon size={16} name="user" style={{ color: SIDEBAR.muted }} aria-hidden />
                          <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', color: SIDEBAR.muted }}>
                            {standard}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }} aria-label={t('results.driverActivity.wheelchairPassengersAria', { count: wheelchair })}>
                          <Icon size={16} name="universal-access" style={{ color: SIDEBAR.muted }} aria-hidden />
                          <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', color: SIDEBAR.muted }}>
                            {wheelchair}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default DriverActivityDetailPanel;
